'use client';

import { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';

export type Page = 'home' | 'login' | 'signup' | 'pricing' | 'verify-email' | 'verify-login' | 'create-store' | 'pos' | 'shipping' | 'dashboard' | 'payments' | 'inventory' | 'marketing' | 'themes';

const SUPPORTED_LOCALES = ['ar', 'en', 'fr'];

interface NavigationContextType {
  currentPage: Page;
  navigateTo: (page: Page) => void;
  locale: string;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export function NavigationProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();

  // Extract locale from pathname (e.g., /en/pricing -> en)
  const locale = useMemo(() => {
    if (!pathname) return 'ar';
    const segments = pathname.split('/').filter(Boolean);
    const firstSegment = segments[0];
    if (SUPPORTED_LOCALES.includes(firstSegment)) {
      return firstSegment;
    }
    return 'ar'; // default locale
  }, [pathname]);

  // Map pathname to Page type (remove locale prefix)
  const getCurrentPage = useCallback((): Page => {
    if (!pathname) return 'home';
    const segments = pathname.split('/').filter(Boolean);
    // Remove locale from segments
    if (SUPPORTED_LOCALES.includes(segments[0])) {
      segments.shift();
    }
    if (segments.length === 0) return 'home';
    return segments[0] as Page || 'home';
  }, [pathname]);

  const navigateTo = useCallback((page: Page) => {
    // Include current locale in the route
    const route = page === 'home' ? `/${locale}` : `/${locale}/${page}`;
    router.push(route);
  }, [locale, router]);

  const value = useMemo(() => ({
    currentPage: getCurrentPage(),
    navigateTo,
    locale
  }), [getCurrentPage, navigateTo, locale]);

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}